// src/pages/profesor/DocumentosProfesorPage.tsx
/* ──────────────────────────────────────────────────────────────
   DocumentosProfesorPage.tsx
   · Pestaña «Pendientes de firma»  → estado PENDING
   · Pestaña «Documentos»           → estado SIGNED (incluye Justificantes)
   · Filtros:  Tipo doc. · Buscar… · Desde · Hasta · Limpiar
   · Tablas   MUI DataGrid  (orden, paginación, densidad compacta)
   · ✔  Preview / Download con AUTH (blob)
   · ✔  Feedback visual: spinner por fila y bloqueo de botones
   · ✔  Columna “Firma” solo en pestaña Documentos
   ────────────────────────────────────────────────────────────── */

import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import { DataGrid, esES, GridColDef } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import { useDocumentTypes } from "../../hooks/documentos/useDocumentTypes";

/* ═════════ tipos ═════════ */
interface DocumentoRow {
  id: number;
  nombre: string;
  tipo: string;
  fecha: string | null;
  comentario: string | null;
  hasPdf: boolean;
  isJustificante: boolean;
  requiresSignature: boolean; // ← NUEVO
}

interface Filters {
  docTypeId: string;
  q: string;
  from: string; // YYYY-MM-DD o ""
  to: string; // idem
}

/* ═════════ hook documentos ═════════ */
function useDocumentos(uid: number, estado: "PENDING" | "SIGNED", filters: Filters) {
  const [data, setData] = useState<DocumentoRow[]>([]);
  const [loading, setLoad] = useState(false);
  const [error, setErr] = useState<Error | null>(null);

  useEffect(() => {
    if (!uid) return;
    let abort = false;

    (async () => {
      try {
        setLoad(true);
        const { data: res } = await api.get("/documentos/historico", {
          params: {
            userId: uid,
            estado,
            docTypeId: filters.docTypeId || undefined,
            q: filters.q || undefined,
          },
        });

        const src = Array.isArray(res?.entities) ? res.entities : [];

        if (!abort) {
          const mapped: DocumentoRow[] = src.map((d: any) => {
            const docTypeName = d.docType?.nombre ?? "";
            const isJustificante = docTypeName.toLowerCase() === "justificante";
            const requiresSignature = !!d.docType?.needsSignature;

            const fecha =
              estado === "SIGNED"
                ? (d.dd_signed_at ?? d.signedAt ?? null)
                : (d.sr_sent_at ?? d.sent_at ?? d.createdAt ?? null);

            return {
              id: d.docId,
              nombre: d.originalName,
              tipo: docTypeName,
              fecha,
              comentario: d.comentario ?? null, // solo el visible, NO el interno
              hasPdf: isJustificante || estado === "SIGNED",
              isJustificante,
              requiresSignature,
            };
          });

          /* filtro Desde – Hasta (cliente) */
          const from = filters.from ? dayjs(filters.from, "YYYY-MM-DD") : null;
          const to = filters.to ? dayjs(filters.to, "YYYY-MM-DD") : null;

          setData(
            mapped.filter((r) => {
              if (!r.fecha) return false;
              const d = dayjs(r.fecha);
              if (from && d.isBefore(from, "day")) return false;
              if (to && d.isAfter(to, "day")) return false;
              return true;
            })
          );
        }
      } catch (e: any) {
        if (!abort) setErr(e);
      } finally {
        if (!abort) setLoad(false);
      }
    })();

    return () => {
      abort = true;
    };
  }, [uid, estado, filters]);

  return { data, loading, error };
}

/* ═════════ helpers preview / download (XHR + Blob con token) ═════════ */
async function fetchDocumentoBlob(id: number) {
  const res = await api.get(`/documentos/${id}/pdf`, {
    params: { stream: 1 }, // fuerza stream en lugar de redirect
    responseType: "blob",
  });
  return res.data as Blob;
}

async function fetchAdjuntoBlob(id: number) {
  const res = await api.get(`/adjuntos/${id}`, { responseType: "blob" });
  return res.data as Blob;
}

function openBlobInNewTab(blob: Blob) {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = (filename || "documento.pdf").replace(/\.pdf$/i, "") + ".pdf";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/* ═════════ barra de filtros ═════════ */
function FiltrosBar({ value, onChange }: { value: Filters; onChange: (f: Filters) => void }) {
  const { t } = useTranslation<"translation">();
  const { data: tipos = [] } = useDocumentTypes();

  const clear = () => onChange({ docTypeId: "", q: "", from: "", to: "" });
  const disabled = !value.docTypeId && !value.q && !value.from && !value.to;

  return (
    <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }}>
      {/* tipo */}
      <TextField
        select
        size="small"
        sx={{ minWidth: 200 }}
        label={t("documentos.filtro.tipo", "Tipo doc.")}
        value={value.docTypeId}
        onChange={(e) => onChange({ ...value, docTypeId: e.target.value })}
      >
        <MenuItem value="">{t("comun.todos", "Todos")}</MenuItem>
        {tipos.map((t) => (
          <MenuItem key={t.docTypeId} value={String(t.docTypeId)}>
            {t.nombre}
          </MenuItem>
        ))}
      </TextField>

      {/* desde */}
      <TextField
        type="date"
        size="small"
        sx={{ width: 155 }}
        label={t("documentos.filtro.desde", "Desde")}
        InputLabelProps={{ shrink: true }}
        value={value.from}
        onChange={(e) => onChange({ ...value, from: e.target.value })}
      />

      {/* hasta */}
      <TextField
        type="date"
        size="small"
        sx={{ width: 155 }}
        label={t("documentos.filtro.hasta", "Hasta")}
        InputLabelProps={{ shrink: true }}
        value={value.to}
        onChange={(e) => onChange({ ...value, to: e.target.value })}
      />

      {/* buscar */}
      <TextField
        size="small"
        sx={{ minWidth: 220, flex: 1 }}
        label={t("documentos.filtro.buscar", "Buscar…")}
        value={value.q}
        onChange={(e) => onChange({ ...value, q: e.target.value })}
      />

      <Button size="small" disabled={disabled} onClick={clear}>
        {t("comun.limpiar", "Limpiar")}
      </Button>
    </Stack>
  );
}

/* ═════════ página principal ═════════ */
export default function DocumentosProfesorPage() {
  const { t } = useTranslation<"translation">();
  const { user } = useAuth();
  const uid = user?.id ?? 0;

  const [tab, setTab] = useState(0); // 0 = pendientes · 1 = documentos (antes «Firmados»)
  const [filters, setFilters] = useState<Filters>({
    docTypeId: "",
    q: "",
    from: "",
    to: "",
  });

  const { data: pend, loading: loadP, error: errorP } = useDocumentos(uid, "PENDING", filters);
  const { data: docs, loading: loadF, error: errorF } = useDocumentos(uid, "SIGNED", filters);

  /* loader por fila: 'preview' | 'download' */
  const [busy, setBusy] = useState<Record<number, "preview" | "download">>({});
  const setRowBusy = (id: number, kind: "preview" | "download") =>
    setBusy((prev) => ({ ...prev, [id]: kind }));
  const clearRowBusy = (id: number) =>
    setBusy((prev) => {
      const c = { ...prev };
      delete c[id];
      return c;
    });

  /* ¿hay algún comentario para mostrar? */
  const showComment = [...pend, ...docs].some((r) => r.comentario);

  /* columnas base */
  const baseCols: GridColDef[] = useMemo(
    () => [
      {
        field: "fecha",
        headerName: t("documentos.tabla.fecha", "Fecha") as string,
        width: 130,
        valueGetter: ({ value }) => (value ? dayjs(value).format("DD/MM/YYYY") : ""),
        sortComparator: (a, b) => (a || "").localeCompare(b || ""),
      },
      {
        field: "nombre",
        headerName: t("documentos.tabla.archivo", "Archivo") as string,
        flex: 1,
      },
      {
        field: "tipo",
        headerName: t("documentos.tabla.tipo", "Tipo") as string,
        width: 200,
      },
      {
        field: "comentario",
        headerName: t("documentos.tabla.comentario", "Comentario") as string,
        flex: 1,
        renderCell: ({ value }) => value || "—",
        hide: !showComment,
      },
    ],
    [t, showComment]
  );

  /* columna de acciones (compartida) */
  const actionsCol: GridColDef = useMemo(
    () => ({
      field: "actions",
      headerName: "",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => {
        const isBusy = !!busy[row.id];
        const preview = async () => {
          setRowBusy(row.id, "preview");
          try {
            const blob = row.isJustificante
              ? await fetchAdjuntoBlob(row.id)
              : await fetchDocumentoBlob(row.id);
            openBlobInNewTab(blob);
          } catch {
            /* noop */
          } finally {
            clearRowBusy(row.id);
          }
        };
        const download = async () => {
          setRowBusy(row.id, "download");
          try {
            const blob = row.isJustificante
              ? await fetchAdjuntoBlob(row.id)
              : await fetchDocumentoBlob(row.id);
            downloadBlob(blob, row.nombre);
          } catch {
            /* noop */
          } finally {
            clearRowBusy(row.id);
          }
        };
        return (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={preview}
              disabled={!row.hasPdf || isBusy}
              aria-label="ver"
            >
              {busy[row.id] === "preview" ? (
                <CircularProgress size={16} />
              ) : (
                <VisibilityIcon fontSize="inherit" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={download}
              disabled={!row.hasPdf || isBusy}
              aria-label="descargar"
            >
              {busy[row.id] === "download" ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon fontSize="inherit" />
              )}
            </IconButton>
          </Stack>
        );
      },
    }),
    [busy]
  );

  /* columna “Firma” (solo pestaña Documentos) */
  const firmaCol: GridColDef = useMemo(
    () => ({
      field: "firma",
      headerName: "Firma",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => {
        if (!row.requiresSignature) return ""; // vacío cuando NO requiere firma
        // Si requiere firma: mostrar estado según fecha (por robustez)
        const firmado = !!row.fecha;
        return (
          <Chip
            size="small"
            label={firmado ? "Firmado" : "Pendiente"}
            color={firmado ? ("success" as any) : ("warning" as any)}
          />
        );
      },
    }),
    []
  );

  /* columnas según pestaña */
  const colsPend = useMemo<GridColDef[]>(() => [...baseCols, actionsCol], [baseCols, actionsCol]);
  const colsDocs = useMemo<GridColDef[]>(
    () => [...baseCols, firmaCol, actionsCol],
    [baseCols, firmaCol, actionsCol]
  );

  /* UI */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("documentos.titulo", "Mis documentos")}</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label={t("documentos.tabs.pendientes", "Pendientes de firma")} />
        <Tab label={t("documentos.tabs.documentos", "Documentos")} />
      </Tabs>

      <FiltrosBar value={filters} onChange={setFilters} />

      {/* pendientes */}
      {tab === 0 && (
        <>
          {loadP && <CircularProgress />}
          {errorP && <Alert severity="error">{errorP.message}</Alert>}
          {!loadP && !errorP && (
            <Paper sx={{ overflowX: "auto" }}>
              <DataGrid
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                rows={pend}
                columns={colsPend}
                autoHeight
                density="compact"
                pageSize={25}
                rowsPerPageOptions={[25, 50]}
                disableSelectionOnClick
              />
            </Paper>
          )}
        </>
      )}

      {/* documentos (antes “Firmados”) */}
      {tab === 1 && (
        <>
          {loadF && <CircularProgress />}
          {errorF && <Alert severity="error">{errorF.message}</Alert>}
          {!loadF && !errorF && (
            <Paper sx={{ overflowX: "auto" }}>
              <DataGrid
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                rows={docs}
                columns={colsDocs}
                autoHeight
                density="compact"
                pageSize={25}
                rowsPerPageOptions={[25, 50]}
                disableSelectionOnClick
              />
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}
